/**
 * Simple steps: theme carousel on mobile only (FoxTheme.Carousel / Swiper).
 */
if (!customElements.get('simple-steps-mobile-slider')) {
  customElements.define(
    'simple-steps-mobile-slider',
    class SimpleStepsMobileSlider extends HTMLElement {
      constructor() {
        super();
        this.sliderInstance = false;
        this.initTimeout = null;
        this.mediaListeners = [];
      }

      connectedCallback() {
        this.sliderWrapper = this.querySelector('.simple-steps__list');
        this.paginationEl = this.querySelector('.simple-steps-mobile-slider__pagination');
        this.sectionId = this.dataset.sectionId;
        this.slidesPerView = parseFloat(this.dataset.slidesPerView) || 1;
        this.gap = parseInt(this.dataset.gap, 10);
        if (Number.isNaN(this.gap)) this.gap = 16;

        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
          this._onBlockSelect = (e) => {
            if (e.detail.sectionId != this.sectionId) return;
            if (!this.sliderInstance || !this.sliderInstance.slider) return;
            const target = e.target;
            const index = Number(target.dataset.stepIndex);
            if (Number.isNaN(index)) return;
            this.sliderInstance.slider.slideTo(index);
          };
          document.addEventListener('shopify:block:select', this._onBlockSelect);
        }

        this.waitForDependencies().then(() => this.init());

        this.resizeObserver = new ResizeObserver(() => {
          if (this.initTimeout) clearTimeout(this.initTimeout);
          this.initTimeout = setTimeout(() => this.init(), 100);
        });
        this.resizeObserver.observe(this);

        document.addEventListener('matchMobile', () => {
          if (this.initTimeout) clearTimeout(this.initTimeout);
          this.initTimeout = setTimeout(() => this.init(), 100);
        });
        document.addEventListener('unmatchMobile', () => {
          if (this.initTimeout) clearTimeout(this.initTimeout);
          this.initTimeout = setTimeout(() => this.init(), 100);
        });
      }

      disconnectedCallback() {
        if (this._onBlockSelect) {
          document.removeEventListener('shopify:block:select', this._onBlockSelect);
        }
        this.removeMediaListeners();
        if (this.initTimeout) clearTimeout(this.initTimeout);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        this.destroySlider();
      }

      removeMediaListeners() {
        if (!this.mediaListeners.length) return;
        this.mediaListeners.forEach(({ element, eventName, handler }) => {
          element.removeEventListener(eventName, handler);
        });
        this.mediaListeners = [];
      }

      scheduleSliderRecalc() {
        if (!this.sliderInstance || !this.sliderInstance.slider) return;
        const swiper = this.sliderInstance.slider;
        requestAnimationFrame(() => {
          swiper.updateSlides();
          swiper.updateProgress();
          swiper.updateSlidesClasses();
          swiper.updateSize();
          swiper.slideToClosest(0, false);
        });
      }

      bindMediaRecalcListeners() {
        this.removeMediaListeners();
        const mediaEls = this.querySelectorAll('img, video');
        mediaEls.forEach((el) => {
          const eventName = el.tagName === 'VIDEO' ? 'loadedmetadata' : 'load';
          const handler = () => this.scheduleSliderRecalc();
          el.addEventListener(eventName, handler, { passive: true });
          this.mediaListeners.push({ element: el, eventName, handler });
        });
      }

      waitForDependencies() {
        return new Promise((resolve) => {
          if (window.FoxTheme && window.FoxTheme.Carousel && window.FoxTheme.config) {
            resolve();
            return;
          }
          let attempts = 0;
          const t = setInterval(() => {
            attempts++;
            if (window.FoxTheme && window.FoxTheme.Carousel && window.FoxTheme.config) {
              clearInterval(t);
              resolve();
            } else if (attempts > 50) {
              clearInterval(t);
              resolve();
            }
          }, 100);
        });
      }

      init() {
        const isMobile =
          window.FoxTheme && window.FoxTheme.config && typeof window.FoxTheme.config.mqlMobile === 'boolean'
            ? window.FoxTheme.config.mqlMobile
            : window.matchMedia('(max-width: 767px)').matches;

        if (isMobile) this.initSlider();
        else this.destroySlider();
      }

      destroySlider() {
        this.classList.remove('swiper');
        if (this.sliderWrapper) {
          this.sliderWrapper.classList.remove('swiper-wrapper');
        }
        if (typeof this.sliderInstance === 'object' && this.sliderInstance) {
          try {
            this.sliderInstance.slider.destroy(true, true);
          } catch (e) {}
          this.sliderInstance = false;
        }
      }

      initSlider() {
        if (!window.FoxTheme || !window.FoxTheme.Carousel) return;
        if (!this.sliderWrapper) return;

        const slideCount = this.sliderWrapper.querySelectorAll('.simple-steps__block.swiper-slide').length;
        if (slideCount === 0) return;

        if (typeof this.sliderInstance === 'object' && this.sliderInstance) {
          try {
            this.sliderInstance.slider.destroy(true, true);
          } catch (e) {}
          this.sliderInstance = false;
        }

        const slidesPerView = 1;
        const spaceBetween = this.gap;

        const opts = {
          slidesPerView,
          slidesPerGroup: 1,
          spaceBetween,
          centeredSlides: false,
          centeredSlidesBounds: false,
          freeMode: false,
          initialSlide: 0,
          normalizeSlideIndex: true,
          observer: true,
          observeParents: true,
          observeSlideChildren: true,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
          watchSlidesProgress: true,
          pagination: this.paginationEl
            ? {
                el: this.paginationEl,
                clickable: true,
              }
            : false,
          loop: false,
          threshold: 2,
          speed: 350,
          longSwipesRatio: 0.2,
          longSwipesMs: 200,
          followFinger: true,
          watchOverflow: true,
          roundLengths: true,
          on: {
            init(swiper) {
              swiper.slideTo(0, 0, false);
              swiper.updateSlides();
              swiper.updateProgress();
              swiper.updateSlidesClasses();
              swiper.updateSize();
            },
          },
        };

        this.classList.add('swiper');
        this.sliderWrapper.classList.add('swiper-wrapper');

        this.sliderInstance = new window.FoxTheme.Carousel(this, opts);
        this.sliderInstance.init();
        if (this.sliderInstance && this.sliderInstance.slider) {
          this.sliderInstance.slider.slideTo(0, 0, false);
        }
        this.bindMediaRecalcListeners();
      }
    }
  );
}
